<template>
  <ModalView :isVisible="isModalVisible" :title="'Enter your shipping details'" contentClass="checkout-modal" @close="closeModal">
    <div class="form-checkout-container">
      <!-- VeeValidate Form -->
      <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }">
        <div class="checkout-row">
          <div>
            <Field name="firstName" v-slot="{ field, meta }">
              <input type="text" placeholder="First Name" v-bind="field" v-model="localAddress.firstName"
                class="form-control" :class="{ 'error-border': errors.firstName || (meta.touched && !meta.valid) }" />
              <ErrorMessage class="error" name="firstName" />
            </Field>
          </div>
          <div>
            <Field name="lastName" v-slot="{ field, meta }">
              <input type="text" placeholder="Last Name" v-bind="field" v-model="localAddress.lastName"
                class="form-control" :class="{ 'error-border': errors.lastName || (meta.touched && !meta.valid) }" />
              <ErrorMessage class="error" name="lastName" />
            </Field>
          </div>
        </div>
        <div>
          <Field name="lineOne" v-slot="{ field, meta }">
            <input type="text" placeholder="Address" v-bind="field" v-model="localAddress.lineOne" class="form-control"
              :class="{ 'error-border': errors.lineOne || (meta.touched && !meta.valid) }" />
            <ErrorMessage class="error" name="lineOne" />
          </Field>
        </div>

        <div>
          <Field name="countryCode" v-slot="{ meta }">
            <CountryCodePicker v-if="isForKit" :modelValue="localAddress.countryCode" :prevented-countries="['FR']"
              :region="region || null"
              class="country-dropdown" :class="{ 'error-border': errors.countryCode || (meta.touched && !meta.valid) }"
              showChoose @update:modelValue="handleCountryUpdate" />
            <CountryCodePicker v-else :modelValue="localAddress.countryCode"
              :region="region || null"
              class="country-dropdown"
              :class="{ 'error-border': errors.countryCode || (meta.touched && !meta.valid) }" showChoose
              @update:modelValue="handleCountryUpdate" />
            <ErrorMessage class="error c-error" name="countryCode" />
          </Field>
        </div>
        <!-- State field (only for USA region) -->
        <div v-if="region === 'USA'" style="margin-bottom: 15px; margin-top: 15px;">
          <Field name="state" v-slot="{ field, meta }">
            <select
              :value="localAddress.state"
              @change="(e) => { localAddress.state = e.target.value; field.onChange(e.target.value); }"
              class="form-control"
              :class="{ 'error-border': errors.state || (meta.touched && !meta.valid) }">
              <option value="">State</option>
              <option v-for="s in usStates" :key="s.code" :value="s.code">{{ s.name }}</option>
            </select>
            <ErrorMessage class="error" name="state" />
          </Field>
        </div>
        <div class="checkout-row margin-top">
          <div>
            <Field name="city" v-slot="{ field, meta }">
              <input type="text" placeholder="City" v-bind="field" v-model="localAddress.city" class="form-control"
                :class="{ 'error-border': errors.city || (meta.touched && !meta.valid) }" />
              <ErrorMessage class="error" name="city" />
            </Field>
          </div>
          <div>
            <Field name="zip" v-slot="{ field, meta }">
              <input type="text" placeholder="Zip Code" v-bind="field" v-model="localAddress.zip" class="form-control"
                :class="{ 'error-border': errors.zip || (meta.touched && !meta.valid) }" />
              <ErrorMessage class="error" name="zip" />
            </Field>
          </div>
        </div>
        <div>
          <Field name="phone" v-slot="{ field, meta }">
            <input type="text" placeholder="Phone Number" v-bind="field" v-model="localAddress.phone"
              class="form-control" :class="{ 'error-border': errors.phone || (meta.touched && !meta.valid) }" />
            <ErrorMessage class="error" name="phone" />
          </Field>
        </div>
        <div class="terms-agreement">
          By continuing to checkout you are agreeing to our
          <a href="https://livingdna.com/uk/legal/Terms-Of-Use" target="_new" rel="noopener">
            Terms & Conditions
          </a>
          &
          <a href="https://livingdna.com/uk/Privacy-Policy" target="_new" rel="noopener">
            Privacy Policy
          </a>
        </div>
        <div class="action-cta">
          <!-- Wrapped: BloodTestDetails.scss styles .action-cta's direct buttons by position -->
          <div class="checkout-actions">
            <button type="submit" class="ldna-btn" :disabled="isSubmitting">
              {{ isSubmitting ? 'Processing...' : 'Continue' }}
            </button>
            <button type="button" class="ldna-link" @click="backModal">Back</button>
          </div>
        </div>
      </Form>
    </div>
  </ModalView>
</template>

<script>
import { ref, watch, onMounted, nextTick } from 'vue';
import { Form, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import CountryCodePicker from "./CountryCodePicker.vue";
import ModalView from "@/components/Modal/ModalView.vue";
import usStates from "@/usStates";
import "@/styles/pages/BloodTestDetails.scss";

export default {
  name: "CheckoutModal",
  components: {
    ModalView,
    CountryCodePicker,
    Form,
    Field,
    ErrorMessage,
  },
  props: {
    isModalVisible: {
      type: Boolean,
      default: false
    },
    contactDetails: {
      type: Object,
      default: () => ({})
    },
    isForKit: {
      type: Boolean,
      default: false
    },
    region: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'back', 'openPaymentModal', 'update:address'],
  setup(props, { emit }) {
    const localAddress = ref({
      firstName: "",
      lastName: "",
      lineOne: "",
      city: "",
      zip: "",
      countryCode: "",
      state: "",
      phone: ""
    });

    // Track if we're updating from parent to prevent loops
    const isUpdatingFromParent = ref(false);
    const isUpdatingFromLocal = ref(false);

    // Define initializeLocalAddress first
    const initializeLocalAddress = () => {
      isUpdatingFromParent.value = true;
      // For USA region the country is always US; pre-fill if not already set.
      const defaultCountry = props.region === 'USA' ? 'US' : (props.contactDetails?.countryCode || "");
      localAddress.value = {
        firstName: props.contactDetails?.firstName || "",
        lastName: props.contactDetails?.lastName || "",
        lineOne: props.contactDetails?.lineOne || "",
        city: props.contactDetails?.city || "",
        zip: props.contactDetails?.zip || "",
        countryCode: props.contactDetails?.countryCode || defaultCountry,
        state: props.contactDetails?.state || "",
        phone: props.contactDetails?.phone || ""
      };

      // Reset the flag after a brief delay
      setTimeout(() => {
        isUpdatingFromParent.value = false;
      }, 50);
    };

    // Initialize immediately when component is created
    onMounted(() => {
      initializeLocalAddress();
    });

    // Initialize localAddress when modal becomes visible
    watch(() => props.isModalVisible, (isVisible) => {
      if (isVisible) {
        // Use nextTick to ensure the modal is fully rendered
        nextTick(() => {
          initializeLocalAddress();
        });
      }
    });

    // Update localAddress when contactDetails prop changes
    watch(() => props.contactDetails, (newContactDetails) => {
      if (newContactDetails && !isUpdatingFromLocal.value) {
        // Only update if the change didn't originate from localAddress
        initializeLocalAddress();
      }
    }, { deep: true });

    // Emit updates when localAddress changes, but debounce to prevent loops
    let updateTimeout = null;
    watch(localAddress, (newAddress) => {
      if (isUpdatingFromParent.value) {
        // Don't emit if we're updating from parent
        return;
      }

      if (updateTimeout) clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        isUpdatingFromLocal.value = true;
        emit('update:address', { ...newAddress });

        // Reset the flag after a brief delay
        setTimeout(() => {
          isUpdatingFromLocal.value = false;
        }, 50);
      }, 300); // Increased debounce time
    }, { deep: true });

    const handleCountryUpdate = (value) => {
      localAddress.value.countryCode = value;
      if (value !== 'US') {
        localAddress.value.state = '';
      }
    };

    const schema = yup.object().shape({
      firstName: yup.string().required("First name is required").trim(),
      lastName: yup.string().required("Last name is required").trim(),
      lineOne: yup.string().required("Address is required").trim(),
      countryCode: yup.string().required("Country is required"),
      city: yup.string().required("City is required").trim(),
      zip: yup.string().required("Zip code is required").trim(),
      // State field: required only for USA region
      state: props.region === 'USA'
        ? yup.string().required("State is required")
        : yup.string().notRequired().nullable(),
      phone: yup
        .string()
        .required("Phone number is required")
        .matches(/^[0-9+\-\s()]+$/, "Please enter a valid phone number")
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number must not exceed 15 digits")
    });

    const onSubmit = async (values, { setFieldError }) => {
      try {
        // Validate the form values against the schema
        await schema.validate(values, { abortEarly: false });

        // If validation passes, emit the event
        emit("openPaymentModal", values);
        closeModal();
      } catch (validationErrors) {
        // Handle validation errors
        if (validationErrors.inner) {
          validationErrors.inner.forEach((error) => {
            setFieldError(error.path, error.message);
          });
        }
      }
    };

    const closeModal = () => {
      emit("close");
    };

    const backModal = () => {
      emit("back");
    };

    return {
      localAddress,
      schema,
      onSubmit,
      closeModal,
      backModal,
      handleCountryUpdate,
      usStates,
    };
  }
};
</script>

<style scoped>
.form-checkout-container {
  padding: 20px;
}

.checkout-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.checkout-row>div {
  flex: 1;
}

.margin-top {
  margin-top: 15px;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  /* 16px: iPhones zoom the page into smaller fields when they're tapped */
  font-size: 16px;
  transition: border-color 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
}

.form-control.error-border {
  border-color: #ff3860;
}

.error {
  color: #ff3860;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.c-error {
  margin-top: 8px;
}

.action-cta {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  gap: 10px;
}

.checkout-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.country-dropdown {
  width: 100%;
}

.country-dropdown.error-border {
  border-color: #ff3860;
}

.terms-agreement {
  margin: 15px 0;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.5;
  text-align: left;
}

.terms-agreement a {
  color: #007bff;
  text-decoration: none;
}

.terms-agreement a:hover {
  text-decoration: underline;
}
</style>
